/**
 * Admin Stats `/admin/stats` — a range-based operational dashboard over the
 * scheduled 4h snapshots. Access is enforced by getAdminStatsFn; the route
 * guard is cosmetic.
 *
 * SECURITY PIN: narrative text and GitHub titles are untrusted external text.
 * They render as plain React text only — never markdown/HTML.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ACTIVITY_RANGES,
  type ActivityRangeKey,
  type AdminDbQueryRow,
  type AdminFirstDraftFeedbackRow,
  type AdminGameCostRow,
  type AdminStatsSourceStatus,
} from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useCopyToClipboard } from '../hooks/useCopyToClipboard.js';
import { Mascot } from '../components/Mascot.js';
import type { MascotContext } from '../components/mascotManifest.js';
import { DonutChart, LineChart, WindowBarChart } from './AdminStatsCharts.js';
import { AdminStatsInfoTip } from './AdminStatsInfoTip.js';
import { serviceDescription } from './adminStatsServices.js';
import { ActivitySummary } from './ActivitySummary.js';
import {
  aggregateAdminStats,
  groupCosts,
  type AdminStatsSnapshotRow,
  type CostDimension,
} from './adminStatsModel.js';

const whole = new Intl.NumberFormat('en-US');
const compact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const num = (value: number | null | undefined) =>
  value == null ? '—' : whole.format(Math.round(value));
const usd = (value: number | null | undefined) =>
  value == null ? '—' : value >= 100 ? `$${whole.format(Math.round(value))}` : `$${value.toFixed(2)}`;
const ms = (value: number | null | undefined) =>
  value == null ? '—' : value >= 1000 ? `${(value / 1000).toFixed(1)}s` : `${Math.round(value)}ms`;
const pct = (value: number | null | undefined, digits = 0) =>
  value == null ? '—' : `${(value * 100).toFixed(digits)}%`;
const per = (cost: number | undefined, count: number | undefined) =>
  cost == null || count == null || count === 0 ? '—' : usd(cost / count);

function bytes(value: number | null | undefined): string {
  if (value == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let scaled = Math.abs(value);
  let index = 0;
  while (scaled >= 1024 && index < units.length - 1) {
    scaled /= 1024;
    index += 1;
  }
  const prefix = value < 0 ? '−' : '';
  return `${prefix}${scaled.toFixed(scaled < 10 && index > 0 ? 1 : 0)}${units[index]}`;
}

const clock = (time: number) =>
  new Date(time).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
const shortClock = (time: number) =>
  new Date(time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });

const STATUS_COLOR: Record<AdminStatsSourceStatus['status'], string> = {
  ok: 'var(--good)',
  skipped: 'var(--warn)',
  error: 'var(--bad)',
};

function SourcePill({ label, status }: { label: string; status?: AdminStatsSourceStatus }) {
  const value = status?.status ?? 'skipped';
  return (
    <span className="astats-pill" title={status?.note ?? value}>
      <span className="astats-dot" style={{ background: STATUS_COLOR[value] }} />
      {label} · {value}
    </span>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'good' | 'bad' | 'warn';
}) {
  return (
    <div className="astats-card astats-kpi">
      <div className="astats-card__label">{label}</div>
      <div className={`astats-card__value ${tone ? `astats-tone--${tone}` : ''}`}>{value}</div>
      {sub && <div className="astats-card__sub">{sub}</div>}
    </div>
  );
}

type TabKey = 'overview' | 'costs' | 'product' | 'database' | 'cloud' | 'delivery';
const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'costs', label: 'Generation costs' },
  { key: 'product', label: 'Product' },
  { key: 'database', label: 'Database' },
  { key: 'cloud', label: 'Cloud & logs' },
  { key: 'delivery', label: 'Delivery' },
];

const TAB_INTROS: Record<TabKey, { context: MascotContext; title: string; copy: string }> = {
  overview: {
    context: 'admin-stats-overview',
    title: 'Mission control',
    copy: 'The whole flock at a glance: product motion, spend, traffic, and sharp edges.',
  },
  costs: {
    context: 'admin-stats-costs',
    title: 'The token furnace',
    copy: 'V1 games, normal iterations, and asset generation finally get separate price tags.',
  },
  product: {
    context: 'admin-stats-product',
    title: 'What people actually did',
    copy: 'Window activity next to the all-time scoreboard from the public Stats page.',
  },
  database: {
    context: 'admin-stats-database',
    title: 'Deej vs. the query pile',
    copy: 'Query volume, four-hour bursts, cache behavior, and slow normalized statements.',
  },
  cloud: {
    context: 'admin-stats-cloud',
    title: 'Cloud weather',
    copy: 'Requests, errors, latency, service share, and logs that now stay inside their box.',
  },
  delivery: {
    context: 'admin-stats-delivery',
    title: 'Shipping desk',
    copy: 'PRs, commits, deploys, and admin activity over the same selected window.',
  },
};

function TabIntro({ tab }: { tab: TabKey }) {
  const intro = TAB_INTROS[tab];
  return (
    <div className="astats-tab-intro">
      <div>
        <div className="astats-eyebrow">Deej’s desk</div>
        <h2>{intro.title}</h2>
        <p>{intro.copy}</p>
      </div>
      <Mascot context={intro.context} size={190} quip />
    </div>
  );
}

const GCP_PROJECT = 'djgg-platform';
const GCP_REGION = 'us-central1';
const GITHUB_REPO = 'donjon-bsc/djgg';

function cloudRunUrl(service: string): string {
  return `https://console.cloud.google.com/run/detail/${GCP_REGION}/${encodeURIComponent(service)}/metrics?project=${GCP_PROJECT}`;
}

function logsUrl(service: string, severity?: string): string {
  const filters = [
    'resource.type="cloud_run_revision"',
    `resource.labels.service_name="${service.replace(/["\\]/g, '')}"`,
    ...(severity ? [`severity=${severity}`] : []),
  ];
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(filters.join('\n'))}?project=${GCP_PROJECT}`;
}

const prUrl = (number: number) => `https://github.com/${GITHUB_REPO}/pull/${number}`;

function ServiceName({ service, link = 'run' }: { service: string; link?: 'run' | 'logs' }) {
  return (
    <span className="astats-service-name">
      <a href={link === 'run' ? cloudRunUrl(service) : logsUrl(service)} target="_blank" rel="noreferrer">
        {service}
      </a>
      <AdminStatsInfoTip text={serviceDescription(service)} />
    </span>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <button className="astats-copy" type="button" onClick={() => void copy(value)} aria-label={`Copy ${label}`} title={`Copy ${label}`}>
      {copied ? '✓' : '⧉'}
    </button>
  );
}

function QueryTable({
  rows,
  empty,
  showTotal = false,
}: {
  rows: AdminDbQueryRow[];
  empty: string;
  showTotal?: boolean;
}) {
  return (
    <div className="astats-table-scroll">
      <table className="astats-table astats-query-table">
        <thead>
          <tr>
            <th>normalized query</th>
            <th className="num">calls</th>
            <th className="num">mean</th>
            <th className="num">{showTotal ? 'total' : 'max'}</th>
            <th className="num">rows</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="astats-muted">{empty}</td></tr>}
          {rows.map((query) => {
            const text = query.query || `Normalized SQL unavailable for historical query ${query.queryId}`;
            return (
              <tr key={query.queryId}>
                <td>
                  <span className="astats-query-cell" title={text}>
                    <code>{text}</code>
                    <CopyButton value={text} label="normalized query" />
                  </span>
                </td>
                <td className="num">{num(query.calls)}</td>
                <td className="num">{ms(query.meanExecMs)}</td>
                <td className="num">{ms(showTotal ? query.totalExecMs : query.maxExecMs)}</td>
                <td className="num">{num(query.rows)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RecentLogs({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ insertId: string; timestamp: number; service: string; severity: string; message: string }>;
}) {
  return (
    <div className="panel">
      <div className="astats-panel-heading">
        <h2 className="panel__title">{title}</h2>
        <span className="astats-muted">latest 5 · server-redacted</span>
      </div>
      <div className="astats-log-samples">
        {rows.length === 0 && <span className="astats-muted">No sampled logs in this range.</span>}
        {rows.map((log, index) => (
          <div className="astats-log-sample" key={`${log.insertId}-${log.timestamp}-${index}`}>
            <time>{clock(log.timestamp)}</time>
            <a href={logsUrl(log.service, log.severity)} target="_blank" rel="noreferrer">{log.service}</a>
            <span className={`astats-severity astats-severity--${log.severity.toLowerCase()}`}>{log.severity}</span>
            <code title={log.message}>{log.message}</code>
            <CopyButton value={log.message} label={`${log.severity.toLowerCase()} log`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SystemOverviewDiagram({
  aggregate,
  requestTotal,
  errorTotal,
  totalCost,
}: {
  aggregate: NonNullable<ReturnType<typeof aggregateAdminStats>>;
  requestTotal: number;
  errorTotal: number;
  totalCost: number;
}) {
  const builder = aggregate.services.find((service) => service.service.includes('builder'));
  const gameServer = aggregate.services.find((service) => service.service.includes('game-server'));
  const vendors = groupCosts(aggregate.llmRows, 'provider');
  return (
    <div className="panel astats-system-panel">
      <div className="astats-panel-heading">
        <div>
          <div className="astats-eyebrow">Live architecture</div>
          <h2 className="panel__title">How the flock fits together</h2>
        </div>
        <span className="astats-muted">Values use the selected range</span>
      </div>
      <div className="astats-system" role="img" aria-label="System architecture with selected-range operational statistics">
        <div className="astats-system-node astats-system-node--github">
          <span>GitHub</span><strong>{num(aggregate.github.prsMerged)} merged PRs</strong><small>{num(aggregate.github.commits)} main commits</small>
        </div>
        <div className="astats-system-node astats-system-node--people">
          <span>Players & creators</span><strong>{num(aggregate.productDeltas.pageViews)} page views</strong><small>{num(aggregate.productDeltas.totalPlays)} plays</small>
        </div>
        <div className="astats-system-cloud">
          <div className="astats-system-cloud__title"><span>GCP cloud · {GCP_PROJECT}</span><strong>{num(requestTotal)} requests · {num(errorTotal)} errors</strong></div>
          <div className="astats-system-cloud__services">
            <div><span>Portal + functions</span><strong>{num(aggregate.product?.newSessions)} sessions</strong></div>
            <div><span>Builder</span><strong>{num(builder?.requestCount)} requests</strong><small>{usd(totalCost)} model spend</small></div>
            <div><span>Game server</span><strong>{num(gameServer?.requestCount)} requests</strong><small>{num(aggregate.product?.multiplayerMatches)} matches</small></div>
          </div>
          <small>{num(aggregate.deploys.length)} Cloud Run revisions in range</small>
        </div>
        <div className="astats-system-node astats-system-node--llm">
          <span>LLM vendors</span><strong>{num(vendors.reduce((sum, vendor) => sum + vendor.calls, 0))} calls · {usd(totalCost)}</strong>
          <small>{vendors.map((vendor) => `${vendor.label} ${usd(vendor.costUsd)}`).join(' · ') || 'No model traffic'}</small>
        </div>
        <div className="astats-system-node astats-system-node--db">
          <span>Supabase · Postgres</span><strong>{num(aggregate.queries.totalCalls)} queries</strong><small>{pct(aggregate.queries.cacheHitRatio, 1)} cache hit · {bytes(aggregate.queries.dbSizeBytes)}</small>
        </div>
      </div>
    </div>
  );
}

function CostTable({
  groups,
}: {
  groups: ReturnType<typeof groupCosts>;
}) {
  const total = groups.reduce((sum, group) => sum + group.costUsd, 0);
  return (
    <div className="astats-table-scroll">
      <table className="astats-table">
        <thead>
          <tr>
            <th>group</th>
            <th className="num">model calls</th>
            <th className="num">input total</th>
            <th className="num">output total</th>
            <th className="num">tokens / call</th>
            <th className="num">total cost</th>
            <th className="num">cost / call</th>
            <th className="num">share</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={group.key}>
              <td className="astats-label-cell" title={group.label}>{group.label}</td>
              <td className="num">{num(group.calls)}</td>
              <td className="num">{compact.format(group.inputTokens)}</td>
              <td className="num">{compact.format(group.outputTokens)}</td>
              <td className="num">{group.calls === 0 ? '—' : `${compact.format(group.inputTokens / group.calls)} in · ${compact.format(group.outputTokens / group.calls)} out`}</td>
              <td className="num">{usd(group.costUsd)}</td>
              <td className="num">{group.calls === 0 ? '—' : usd(group.costUsd / group.calls)}</td>
              <td className="num">{total === 0 ? '—' : pct(group.costUsd / total, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const share = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');

/** The keep/discard signal at its drill-down grain. Stated intent, reported
 *  with its population and coverage so silence never reads as approval. */
function FirstDraftFeedbackTable({ rows }: { rows: AdminFirstDraftFeedbackRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="astats-muted">
        No first-draft verdicts yet — rows appear with the first snapshot taken after this feature deployed.
      </p>
    );
  }
  return (
    <div className="astats-table-scroll">
      <table className="astats-table" data-testid="first-draft-feedback">
        <thead>
          <tr>
            <th>family</th>
            <th>recipe</th>
            <th>engine</th>
            <th>cohort</th>
            <th className="num">first drafts live</th>
            <th className="num">first builds failed</th>
            <th className="num">rated</th>
            <th className="num">coverage</th>
            <th className="num">keep</th>
            <th className="num">discard</th>
            <th className="num">keep rate</th>
            <th className="num">admin keep / discard</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={`${row.family}|${row.pipelineId}|${row.engine}|${row.cohort}`}>
              <td className="astats-label-cell">{row.family}</td>
              <td className="astats-label-cell">{row.pipelineId}</td>
              <td className="astats-label-cell">{row.engine}</td>
              <td>{row.cohort}</td>
              <td className="num">{num(row.firstDraftsLive)}</td>
              <td className="num">{num(row.firstDraftsFailed)}</td>
              <td className="num">{num(row.rated)}</td>
              <td className="num">{share(row.rated, row.firstDraftsLive)}</td>
              <td className="num">{num(row.keep)}</td>
              <td className="num">{num(row.discard)}</td>
              <td className="num">{share(row.keep, row.rated)}</td>
              <td className="num">{num(row.adminKeep)} / {num(row.adminDiscard)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GameCostTable({ rows }: { rows: AdminGameCostRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="astats-muted">
        No per-game data yet — it appears with the first snapshot taken after this feature deployed.
      </p>
    );
  }
  return (
    <div className="astats-table-scroll">
      <table className="astats-table">
        <thead>
          <tr>
            <th>game</th>
            <th className="num">V1 build</th>
            <th className="num">iterations</th>
            <th className="num">assets</th>
            <th className="num">concept</th>
            <th className="num">other</th>
            <th className="num">total</th>
            <th className="num">model calls</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.creationId}>
              <td className="astats-label-cell" title={row.title}>
                {row.slug ? <Link to={`/g/${row.slug}`}>{row.title}</Link> : row.title}
              </td>
              <td className="num">{usd(row.v1CostUsd)}</td>
              <td className="num">{usd(row.iterationCostUsd)}</td>
              <td className="num">{usd(row.assetsCostUsd)}</td>
              <td className="num">{usd(row.conceptCostUsd)}</td>
              <td className="num">{usd(row.otherCostUsd)}</td>
              <td className="num">{usd(row.totalCostUsd)}</td>
              <td className="num">{num(row.calls)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminStats() {
  const services = useServices();
  const [snapshots, setSnapshots] = useState<AdminStatsSnapshotRow[] | null>(null);
  const [error, setError] = useState('');
  const [range, setRange] = useState<ActivityRangeKey>('24h');
  const [anchor, setAnchor] = useState(0);
  const [tab, setTab] = useState<TabKey>('overview');
  const [costDimension, setCostDimension] = useState<CostDimension>('lane');
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    let live = true;
    services.callable
      .getAdminStats({ all: true })
      .then((result) => live && setSnapshots(result.snapshots as AdminStatsSnapshotRow[]))
      .catch((reason) => {
        if (live) setError(reason instanceof Error ? reason.message : 'Failed to load admin stats.');
      });
    return () => {
      live = false;
    };
  }, [services]);

  const anchored = useMemo(() => snapshots?.slice(anchor) ?? [], [snapshots, anchor]);
  const aggregate = useMemo(() => aggregateAdminStats(anchored, range), [anchored, range]);
  const costGroups = useMemo(
    () => groupCosts(aggregate?.llmRows ?? [], costDimension),
    [aggregate, costDimension],
  );
  const laneGroups = useMemo(
    () => groupCosts(aggregate?.llmRows ?? [], 'lane'),
    [aggregate],
  );
  const lane = (key: string) => laneGroups.find((group) => group.key === key);
  const totalCost = laneGroups.reduce((sum, group) => sum + group.costUsd, 0);
  const requestTotal = aggregate?.services.reduce((sum, service) => sum + service.requestCount, 0) ?? 0;
  const errorTotal = aggregate?.services.reduce((sum, service) => sum + service.errorCount, 0) ?? 0;
  // Every heading on this page reads this label, so it counts in words: "1 days"
  // was showing on the 24h range.
  const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;
  const rangeLabel =
    aggregate == null
      ? ''
      : range === 'all'
        ? `all ${aggregate.selected.length} tracked windows`
        : aggregate.coveredHours < 24
        ? plural(aggregate.coveredHours, 'hour')
        : plural(Math.round(aggregate.coveredHours / 24), 'day');

  const severityGroups = useMemo(() => {
    const map = new Map<string, number>();
    for (const log of aggregate?.logs ?? []) map.set(log.severity, (map.get(log.severity) ?? 0) + log.count);
    return [...map.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [aggregate]);

  const productScoreboard = useMemo(() => {
    if (!aggregate) return [];
    const tracked = aggregate.product;
    const totals = aggregate.productTotals;
    const all = range === 'all' && totals != null;
    return [
      ['Games created', all ? totals.gamesCreated : tracked?.gamesCreated],
      ['Native games added', all ? totals.nativeGames : tracked?.nativeGamesAdded],
      ['V1 games live', tracked?.v1GamesLive],
      ['Iterations', all ? totals.gameIterations : tracked?.gameIterations],
      ['Variants generated', all ? totals.assetVariantsGenerated : tracked?.assetVariantsGenerated],
      ['Assets attached', aggregate.productDeltas.assetsAttached],
      ['Multiplayer matches', all ? totals.multiplayerMatches : tracked?.multiplayerMatches],
      ['Total plays', aggregate.productDeltas.totalPlays],
      ['Game views', aggregate.productDeltas.gameViews],
      ['Portal page views', aggregate.productDeltas.pageViews],
      ['New users', all ? totals.totalUsers : tracked?.newUsers],
      ['Unique sessions', all ? totals.uniqueSessions : tracked?.newSessions],
      ['Forks completed', all ? totals.completedForks : tracked?.completedForks],
      ['Likes', all ? totals.totalLikes : tracked?.likes],
      ['Comments', all ? totals.totalComments : tracked?.comments],
    ] as Array<[string, number | null | undefined]>;
  }, [aggregate, range]);

  const warningLogs = (aggregate?.recentLogs ?? [])
    .filter((log) => log.severity === 'WARNING')
    .slice(0, 5);
  const errorLogs = (aggregate?.recentLogs ?? [])
    .filter((log) => ['ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'].includes(log.severity))
    .slice(0, 5);

  return (
    <div className="container astats-page">
      <div className="astats-hero">
        <div>
          <div className="astats-eyebrow">Operational intelligence</div>
          <h1 className="gallery__title">Admin Stats</h1>
          <p className="gallery__subtitle">
            Four-hour source snapshots, regrouped into the window you care about.{' '}
            <Link to="/admin">← Admin</Link>
          </p>
        </div>
        <img
          className="astats-hero__img"
          src="/mascot/admin-stats-hero.webp"
          alt="Deej the pigeon conducting a chaotic orchestra of operational dashboards"
          loading="lazy"
        />
      </div>

      {error && <div className="panel astats-error">{error}</div>}
      {!error && snapshots === null && <div className="panel">Counting queries and pigeon receipts…</div>}
      {!error && snapshots?.length === 0 && (
        <div className="panel">No snapshots yet — the first cron run populates this within four hours.</div>
      )}

      {aggregate && snapshots && (
        <>
          {aggregate.digest?.narrativeBullets?.length ? (
            <button className="astats-ticker" onClick={() => setShowFull(true)} type="button">
              <span className="astats-ticker__track">
                {[...aggregate.digest.narrativeBullets, ...aggregate.digest.narrativeBullets].map((bullet, index) => (
                  <span className="astats-ticker__item" key={`${bullet}-${index}`}>{bullet}</span>
                ))}
              </span>
              <span className="astats-ticker__hint">full analysis ↗</span>
            </button>
          ) : null}

          <div className="astats-controls panel">
            <div className="astats-control-group">
              <span className="astats-control-label">Range</span>
              <div className="astats-segments" aria-label="Stats time range">
                {ACTIVITY_RANGES.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    className={range === option.key ? 'is-active' : ''}
                    onClick={() => setRange(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="astats-anchor">
              <span className="astats-control-label">Window ending</span>
              <select value={anchor} onChange={(event) => setAnchor(Number(event.target.value))}>
                {snapshots.map((snapshot, index) => (
                  <option value={index} key={snapshot.id}>
                    {index === 0 ? 'Latest · ' : ''}{clock(snapshot.windowEnd)}
                  </option>
                ))}
              </select>
            </label>
            <div className="astats-coverage">
              Showing {rangeLabel} · {clock(aggregate.selected.at(-1)!.windowStart)} → {clock(aggregate.latest.windowEnd)}
            </div>
          </div>

          <nav className="astats-tabs" aria-label="Admin stats sections">
            {TABS.map((item) => (
              <button
                type="button"
                key={item.key}
                className={tab === item.key ? 'is-active' : ''}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <TabIntro tab={tab} />

          {tab === 'overview' && (
            <>
              <ActivitySummary range={range} rangeLabel={rangeLabel} atLatest={anchor === 0} />

              <div className="panel">
                <div className="astats-panel-heading">
                  <div>
                    <div className="astats-eyebrow">Selected range</div>
                    <h2 className="panel__title">Mission control</h2>
                  </div>
                  <div className="astats-source-pills">
                    <SourcePill label="Supabase" status={aggregate.latest.provenance.postgres} />
                    <SourcePill label="GitHub" status={aggregate.latest.provenance.github} />
                    <SourcePill label="GCP" status={aggregate.latest.provenance.gcp} />
                    <SourcePill label="Digest" status={aggregate.digest?.provenance.llm} />
                  </div>
                </div>
                <div className="astats-grid">
                  <Kpi label="Cloud requests" value={num(requestTotal)} sub={`${pct(requestTotal ? errorTotal / requestTotal : 0, 2)} errors`} />
                  <Kpi label="DB queries" value={num(aggregate.queries.totalCalls)} sub={`${num(aggregate.queries.avgPerHour)}/hour`} />
                  <Kpi label="LLM spend" value={usd(totalCost)} sub={`${num(aggregate.llmRows.reduce((sum, row) => sum + row.calls, 0))} calls`} />
                  <Kpi label="V1 games live" value={num(aggregate.product?.v1GamesLive)} sub={`${per(lane('game-v1')?.costUsd, aggregate.product?.v1GamesLive)} each`} />
                  <Kpi label="Iterations live" value={num(aggregate.product?.gameIterations)} sub={`${per(lane('iteration')?.costUsd, aggregate.product?.gameIterations)} each`} />
                  <Kpi label="Asset variants" value={num(aggregate.product?.assetVariantsGenerated)} sub={`${usd(lane('assets')?.costUsd)} model cost`} />
                </div>
              </div>

              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">Generation spend mix</h2>
                  <DonutChart
                    ariaLabel="Generation spend by workload"
                    data={laneGroups.map((group) => ({ label: group.label, value: group.costUsd }))}
                    center={<><strong>{usd(totalCost)}</strong><span>selected</span></>}
                  />
                </div>
                <div className="panel">
                  <h2 className="panel__title">Request share by service</h2>
                  <DonutChart
                    ariaLabel="Cloud requests by service"
                    data={aggregate.services.map((service) => ({
                      label: service.service,
                      value: service.requestCount,
                      description: serviceDescription(service.service),
                      href: cloudRunUrl(service.service),
                    }))}
                    center={<><strong>{compact.format(requestTotal)}</strong><span>requests</span></>}
                  />
                </div>
              </div>

              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">LLM spend · 4h windows</h2>
                  <LineChart
                    data={aggregate.points.map((point) => ({ label: shortClock(point.start), value: point.llmCostUsd }))}
                    color="var(--good)"
                    ariaLabel="LLM cost trend"
                    legendLabel="Model spend per window"
                    formatValue={usd}
                  />
                </div>
                <div className="panel">
                  <h2 className="panel__title">Database queries · 4h windows</h2>
                  <WindowBarChart
                    data={aggregate.points.map((point) => ({ label: shortClock(point.start), value: point.queryCalls }))}
                    formatValue={num}
                    ariaLabel="Database query volume by four-hour window"
                    legendLabel="Query calls per window"
                  />
                </div>
              </div>
              <SystemOverviewDiagram aggregate={aggregate} requestTotal={requestTotal} errorTotal={errorTotal} totalCost={totalCost} />
            </>
          )}

          {tab === 'costs' && (
            <>
              <div className="astats-grid astats-priority-grid">
                <Kpi label="V1 game generation" value={usd(lane('game-v1')?.costUsd)} sub={`${num(lane('game-v1')?.calls)} calls · ${per(lane('game-v1')?.costUsd, aggregate.product?.v1GamesLive)} per live V1`} />
                <Kpi label="Normal iterations" value={usd(lane('iteration')?.costUsd)} sub={`${num(lane('iteration')?.calls)} calls · ${per(lane('iteration')?.costUsd, aggregate.product?.gameIterations)} per live iteration`} />
                <Kpi label="Asset generation" value={usd(lane('assets')?.costUsd)} sub={`${num(lane('assets')?.calls)} calls · ${per(lane('assets')?.costUsd, aggregate.product?.assetVariantsGenerated)} per variant`} />
              </div>
              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">Cost share</h2>
                  <DonutChart
                    ariaLabel={`LLM cost grouped by ${costDimension}`}
                    data={costGroups.map((group) => ({ label: group.label, value: group.costUsd }))}
                    center={<><strong>{usd(totalCost)}</strong><span>{rangeLabel}</span></>}
                  />
                </div>
                <div className="panel">
                  <h2 className="panel__title">Spend by 4h window</h2>
                  <WindowBarChart
                    data={aggregate.points.map((point) => ({ label: shortClock(point.start), value: point.llmCostUsd }))}
                    formatValue={usd}
                    ariaLabel="LLM spend by four-hour window"
                    legendLabel="Model spend per window"
                  />
                </div>
              </div>
              <div className="panel">
                <div className="astats-panel-heading">
                  <h2 className="panel__title">Cost explorer</h2>
                  <div className="astats-segments" aria-label="Cost grouping">
                    {(['lane', 'provider', 'model', 'task'] as CostDimension[]).map((dimension) => (
                      <button
                        type="button"
                        key={dimension}
                        className={costDimension === dimension ? 'is-active' : ''}
                        onClick={() => setCostDimension(dimension)}
                      >
                        {dimension === 'lane' ? 'workload' : dimension}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="astats-explainer">
                  Tokens and cost are totals for logged provider calls in the selected range—not one game. A V1 can make several calls for design, generation, continuation, repair, and review. “Cost / call” divides by model requests; the V1 KPI above divides the full V1 lane by successful live V1 outcomes. Image token counts are provider-informed estimates where the image API does not return usage.
                </p>
                <CostTable groups={costGroups} />
              </div>
              <div className="panel">
                <div className="astats-panel-heading">
                  <h2 className="panel__title">Cost per game</h2>
                  <span className="astats-muted">
                    All-time · top {(aggregate.latest.quant.postgres?.topGameCosts ?? []).length} by
                    total · latest snapshot
                  </span>
                </div>
                <p className="astats-explainer">
                  Every logged model call charged to the game, since its creation: the base V1 build,
                  every iteration build, asset/cover generation, and concept analysis. Unlike the
                  panels above, this table ignores the selected range.
                </p>
                <GameCostTable rows={aggregate.latest.quant.postgres?.topGameCosts ?? []} />
              </div>
            </>
          )}

          {tab === 'product' && (
            <>
              <div className="panel">
                <div className="astats-panel-heading">
                  <div>
                    <div className="astats-eyebrow">Selected range</div>
                    <h2 className="panel__title">Product scoreboard · {rangeLabel}</h2>
                  </div>
                  <Link to="/stats">Open public Stats →</Link>
                </div>
                <div className="astats-scoreboard">
                  {productScoreboard.map(([label, value]) => (
                    <div key={label}><strong>{num(value)}</strong><span>{label}</span></div>
                  ))}
                </div>
              </div>
              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">Product activity mix</h2>
                  <DonutChart
                    ariaLabel="Product activity in selected range"
                    data={[
                      { label: 'V1 games live', value: aggregate.product?.v1GamesLive ?? 0 },
                      { label: 'Iterations', value: aggregate.product?.gameIterations ?? 0 },
                      { label: 'Asset variants', value: aggregate.product?.assetVariantsGenerated ?? 0 },
                      { label: 'Matches', value: aggregate.product?.multiplayerMatches ?? 0 },
                      { label: 'Likes', value: aggregate.product?.likes ?? 0 },
                      { label: 'Comments', value: aggregate.product?.comments ?? 0 },
                    ]}
                    center={<><strong>{num(aggregate.points.reduce((sum, point) => sum + (point.productEvents ?? 0), 0))}</strong><span>events</span></>}
                  />
                </div>
                <div className="panel">
                  <h2 className="panel__title">Activity pulse · 4h windows</h2>
                  <WindowBarChart
                    data={aggregate.points.map((point) => ({ label: shortClock(point.start), value: point.productEvents }))}
                    formatValue={num}
                    ariaLabel="Product events by four-hour window"
                    legendLabel="Tracked product events"
                  />
                </div>
              </div>
              <div className="panel">
                <div className="astats-panel-heading">
                  <div>
                    <div className="astats-eyebrow">All-time · sampled at the latest snapshot · stated intent, not retention</div>
                    <h2 className="panel__title">First-draft verdicts · keep or discard</h2>
                  </div>
                </div>
                <p className="astats-muted">
                  A member's one-tap answer to "would you keep iterating on this first draft?", per family, recipe and the engine the first build ran on.
                  Coverage is rated ÷ live first drafts; failed first builds and the bot squad are shown separately so they are never mistaken for kept games.
                </p>
                <FirstDraftFeedbackTable rows={aggregate.latest.quant.postgres?.firstDraftFeedback ?? []} />
              </div>
            </>
          )}

          {tab === 'database' && (
            <>
              <div className="astats-grid">
                <Kpi label="Queries" value={num(aggregate.queries.totalCalls)} sub={`${num(aggregate.queries.avgPerHour)}/hour`} />
                <Kpi label="Peak 4h burst" value={num(aggregate.queries.peakWindowCalls)} sub={aggregate.queries.burstRatio == null ? 'needs two samples' : `${aggregate.queries.burstRatio.toFixed(1)}× the typical window`} tone={aggregate.queries.burstRatio != null && aggregate.queries.burstRatio >= 2 ? 'warn' : undefined} />
                <Kpi label={`Slow calls ≥ 250ms`} value={num(aggregate.queries.slowCalls)} sub={`${num(aggregate.latest.quant.postgres?.dbQueries?.slowStatements)} normalized statements`} tone={(aggregate.queries.slowCalls ?? 0) > 0 ? 'warn' : undefined} />
                <Kpi label="Worst execution" value={ms(aggregate.queries.maxExecMs)} sub="max observed by pg_stat_statements" tone={(aggregate.queries.maxExecMs ?? 0) >= 1000 ? 'bad' : undefined} />
                <Kpi label="Cache hit" value={pct(aggregate.queries.cacheHitRatio, 1)} sub="blocks served without disk read" />
                <Kpi label="Database size" value={bytes(aggregate.queries.dbSizeBytes)} sub={`${bytes(aggregate.queries.dbGrowthBytes)} growth in range`} />
              </div>
              {!aggregate.latest.quant.postgres?.dbQueries?.available && (
                <div className="panel astats-warning">
                  Query telemetry is unavailable: {aggregate.latest.quant.postgres?.dbQueries?.note ?? 'no pg_stat_statements sample'}
                </div>
              )}
              <div className="panel">
                <h2 className="panel__title">Query burst timeline · one bar per 4h</h2>
                <WindowBarChart
                  data={aggregate.points.map((point) => ({ label: shortClock(point.start), value: point.queryCalls }))}
                  formatValue={num}
                  ariaLabel="Database query calls by four-hour window"
                  legendLabel="Query calls per window"
                />
              </div>
              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">Slow normalized statements</h2>
                  <QueryTable rows={aggregate.queries.topSlow} empty="No slow statements in the latest sample." />
                </div>
                <div className="panel">
                  <h2 className="panel__title">Top queries · cumulative execution time</h2>
                  <QueryTable rows={aggregate.queries.topQueries} empty="Top-query text starts with schema-v3 snapshots." showTotal />
                </div>
              </div>
              <div className="panel">
                <h2 className="panel__title">Largest tables</h2>
                <table className="astats-table">
                  <tbody>
                    {(aggregate.latest.quant.postgres?.topTables ?? []).map((table) => (
                      <tr key={table.table}><td className="astats-label-cell">{table.table}</td><td className="num">{bytes(table.totalBytes)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === 'cloud' && (
            <>
              <div className="astats-grid">
                <Kpi label="Requests" value={num(requestTotal)} sub={`${num(requestTotal / Math.max(aggregate.coveredHours, 1))}/hour`} />
                <Kpi label="Errors" value={num(errorTotal)} sub={`${pct(requestTotal ? errorTotal / requestTotal : 0, 2)} of requests`} tone={errorTotal > 0 ? 'bad' : 'good'} />
                <Kpi label="Worst p95" value={ms(Math.max(...aggregate.services.map((service) => service.latencyMsP95 ?? 0), 0))} sub="highest service/window p95" />
                <Kpi label="Queue depth" value={num(aggregate.latest.quant.gcp?.taskQueueDepth)} sub="latest sample" />
                <Kpi label="Deploys" value={num(aggregate.deploys.length)} sub="Cloud Run revisions" />
                <Kpi label="Warning+ logs" value={num(aggregate.logs.filter((log) => !['INFO', 'DEBUG', 'DEFAULT'].includes(log.severity)).reduce((sum, log) => sum + log.count, 0))} sub="selected range" />
              </div>
              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">Request share</h2>
                  <DonutChart
                    ariaLabel="Cloud request share by service"
                    data={aggregate.services.map((service) => ({
                      label: service.service,
                      value: service.requestCount,
                      description: serviceDescription(service.service),
                      href: cloudRunUrl(service.service),
                    }))}
                    center={<><strong>{compact.format(requestTotal)}</strong><span>requests</span></>}
                  />
                </div>
                <div className="panel">
                  <h2 className="panel__title">Log severity mix</h2>
                  <DonutChart
                    ariaLabel="Log entries by severity"
                    data={severityGroups}
                    center={<><strong>{compact.format(severityGroups.reduce((sum, item) => sum + item.value, 0))}</strong><span>entries</span></>}
                  />
                </div>
              </div>
              <div className="panel">
                <h2 className="panel__title">Cloud Run services</h2>
                <div className="astats-table-scroll">
                  <table className="astats-table">
                    <thead><tr><th>service</th><th className="num">requests</th><th className="num">errors</th><th className="num">p50</th><th className="num">p95</th><th className="num">p99</th><th className="num">CPU p95</th><th className="num">memory p95</th></tr></thead>
                    <tbody>
                      {aggregate.services.map((service) => (
                        <tr key={service.service}>
                          <td className="astats-label-cell" title={service.service}><ServiceName service={service.service} /></td>
                          <td className="num">{num(service.requestCount)}</td>
                          <td className={`num ${service.errorCount > 0 ? 'astats-bad-number' : ''}`}>{num(service.errorCount)}</td>
                          <td className="num">{ms(service.latencyMsP50)}</td>
                          <td className="num">{ms(service.latencyMsP95)}</td>
                          <td className="num">{ms(service.latencyMsP99)}</td>
                          <td className="num">{pct(service.cpuUtilP95)}</td>
                          <td className="num">{pct(service.memUtilP95)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="panel">
                <h2 className="panel__title">Log severity by service</h2>
                <div className="astats-log-grid">
                  {aggregate.logs.length === 0 && <span className="astats-muted">No log counts in this range.</span>}
                  {aggregate.logs.map((log) => (
                    <div className="astats-log-row" key={`${log.service}-${log.severity}`}>
                      <span className="astats-log-row__service" title={log.service}>
                        <a href={logsUrl(log.service, log.severity)} target="_blank" rel="noreferrer">{log.service}</a>
                        <AdminStatsInfoTip text={serviceDescription(log.service)} />
                      </span>
                      <span className={`astats-severity astats-severity--${log.severity.toLowerCase()}`}>{log.severity}</span>
                      <strong>{compact.format(log.count)}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div className="astats-chart-grid">
                <RecentLogs title="Latest warnings" rows={warningLogs} />
                <RecentLogs title="Latest errors" rows={errorLogs} />
              </div>
            </>
          )}

          {tab === 'delivery' && (
            <>
              <div className="astats-grid">
                <Kpi label="PRs merged" value={num(aggregate.github.prsMerged)} sub="all target branches" />
                <Kpi label="Commits on main" value={num(aggregate.github.commits)} sub="selected range" />
                <Kpi label="Deploy candidates" value={num(aggregate.github.deployCandidates)} sub="merges into main" />
                <Kpi label="Cloud deploys" value={num(aggregate.deploys.length)} sub="tracked service revisions" />
                <Kpi label="Admin actions" value={num(aggregate.actions.reduce((sum, action) => sum + action.count, 0))} sub={`${aggregate.actions.length} action types`} />
                <Kpi label="Open PRs" value={num(aggregate.latest.quant.github?.prsOpen)} sub="latest sample" />
              </div>
              {aggregate.github.openPrs.length > 0 && (
                <div className="panel">
                  <div className="astats-panel-heading">
                    <h2 className="panel__title">Open PRs</h2>
                    <a href={`https://github.com/${GITHUB_REPO}/pulls`} target="_blank" rel="noreferrer">Open on GitHub →</a>
                  </div>
                  <div className="astats-pr-list">
                    {aggregate.github.openPrs.map((pr) => (
                      <article key={pr.number}>
                        <a href={prUrl(pr.number)} target="_blank" rel="noreferrer">#{pr.number} · {pr.title}</a>
                        <p>{pr.summary || 'No PR description provided.'}</p>
                        <small>{pr.draft ? 'Draft · ' : ''}{pr.author} → {pr.base} · opened {clock(pr.createdAt)}</small>
                      </article>
                    ))}
                  </div>
                </div>
              )}
              <div className="astats-chart-grid">
                <div className="panel">
                  <h2 className="panel__title">Merged PRs</h2>
                  <div className="astats-table-scroll">
                    <table className="astats-table">
                      <thead><tr><th className="num">#</th><th>title</th><th>author</th><th className="num">merge time</th></tr></thead>
                      <tbody>
                        {aggregate.github.mergedPrs.length === 0 && <tr><td colSpan={4} className="astats-muted">No PRs merged in this range.</td></tr>}
                        {aggregate.github.mergedPrs.map((pr) => (
                          <tr key={pr.number}>
                            <td className="num"><a href={prUrl(pr.number)} target="_blank" rel="noreferrer">#{pr.number}</a></td>
                            <td className="astats-label-cell" title={pr.title}><a href={prUrl(pr.number)} target="_blank" rel="noreferrer">{pr.title}</a></td>
                            <td>{pr.author}</td>
                            <td className="num">{pr.hoursToMerge.toFixed(1)}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="panel">
                  <h2 className="panel__title">Action log mix</h2>
                  <DonutChart
                    ariaLabel="Admin actions by type"
                    data={aggregate.actions.map((action) => ({ label: action.action, value: action.count }))}
                    center={<><strong>{num(aggregate.actions.reduce((sum, action) => sum + action.count, 0))}</strong><span>actions</span></>}
                  />
                </div>
              </div>
              <div className="panel">
                <h2 className="panel__title">Deploys</h2>
                <div className="astats-deploy-list">
                  {aggregate.deploys.length === 0 && <span className="astats-muted">No tracked deploys in this range.</span>}
                  {aggregate.deploys.map((deploy) => (
                    <div key={`${deploy.service}-${deploy.revision}`}>
                      <strong><a href={cloudRunUrl(deploy.service)} target="_blank" rel="noreferrer">{deploy.service}</a></strong>
                      <span className="astats-mono">{deploy.revision}</span>
                      <time>{clock(deploy.createTime)}</time>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {showFull && aggregate && (
        <div className="astats-modal__backdrop" onClick={() => setShowFull(false)}>
          <div className="astats-modal" onClick={(event) => event.stopPropagation()}>
            <div className="astats-panel-heading">
              <h2 className="panel__title">
                Window analysis{aggregate.digest ? ` · ${clock(aggregate.digest.windowStart)}–${clock(aggregate.digest.windowEnd)}` : ''}
              </h2>
              <button className="astats-window" type="button" onClick={() => setShowFull(false)}>close ✕</button>
            </div>
            <Mascot context="admin-stats-analysis" size={170} quip />
            {aggregate.digest?.narrative ? (
              <p className="astats-modal__narrative">{aggregate.digest.narrative}</p>
            ) : <p className="astats-muted">No analysis for this window.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
